import article02Image from "../assets/article-02.webp";
import article03Image from "../assets/Article-03.webp";
import article04Image from "../assets/Article-04.webp";
import article05Image from "../assets/article-05-merger.jpg";
import {
  type Article,
  ArticleSectionType,
  PreviewType,
} from "../types/article";

const MOTIVE_SECTION_TITLE_CLASSES = "body-semi-large-semibold";

const ASSET_MANAGEMENT_WHITEPAPER_PDF_URL =
  "https://cdn.prod.website-files.com/68b1b78f229228660e595fb9/693abf2fe29df4af5c7fabdc_MV_wp_Motive%20x%20Amundi_FINAL.pdf";

export const motiveArticles: Article[] = [
  {
    id: "motive-8",
    slug: "motive-ventures-the-evolving-role-of-technology-in-asset-management-from-legacy-to-lagging",
    title:
      "The evolving role of technology in asset management: from legacy to lagging",
    description:
      "Despite being a multi-trillion-dollar industry at the heart of global capital markets, asset management continues to lag in digital transformation. Explore Motive Ventures' perspective on the evolving role of technology in asset management.",
    previewUrl: article02Image,
    previewType: PreviewType.Image,
    pdfUrl: ASSET_MANAGEMENT_WHITEPAPER_PDF_URL,
    author: "Motive Partners",
    publishedDate: "2025-12-01",
    readingTime: 5,
    published: true,
    source: {
      label: "Motive Partners Insights",
      url: "https://www.motivepartners.com/insights/motive-ventures-the-evolving-role-of-technology-in-asset-management-from-legacy-to-lagging",
    },
    sections: [
      {
        id: "section-intro",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Despite being a multi-trillion-dollar industry at the heart of global capital markets, asset management (AM) continues to lag in digital transformation. While sectors such as e-commerce and consumer fintech have embraced cloud-native technologies and AI-driven personalization at scale, many asset managers remain tied to outdated infrastructure, legacy software, and manual workflows.</p>
<p>Read on to explore Motive Ventures' perspective on the evolving role of technology in asset management.</p>`,
        },
      },
    ],
  },
  {
    id: "motive-7",
    slug: "thoughts-on-motive-ioi-collaborative-strategies",
    title: "Thoughts on the Motive IOI & collaborative strategies",
    description:
      "Explore Motive Partners' unique IOI (Investor-Operator-Innovator) model and how collaborative strategies drive success in financial technology investments.",
    previewUrl: "https://player.vimeo.com/video/1124832369",
    previewType: PreviewType.Video,
    author: "Motive Partners",
    publishedDate: "2025-12-01",
    readingTime: 2,
    published: true,
    source: {
      label: "Motive Partners Insights",
      url: "https://www.motivepartners.com/insights/thoughts-on-motive-ioi-collaborative-strategies",
    },
    sections: [
      {
        id: "section-intro",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Collaboration with intent: from insight to action.</p>
<p>Natalya Guseva of the World Economic Forum explains why bringing the right people together matters as technology and capital markets shift. She describes her work with Motive Partners as a practical way to bring stakeholders together, share what works, and turn insight into action. It's a way of working that combines different strengths to build solutions that last.</p>
<p>Her message is direct. Collaboration creates value when it has intent and follow through. Motive Partners connects capital with the people who build and run critical systems, which shortens the distance from idea to delivery. Clear roles and simple ways of working reduce rework and improve execution. When the right groups focus on a defined problem with a plan for delivery, progress accelerates, and choices become clearer.</p>
<blockquote>
  <p>Motive Partners bringing together investors, operators, and innovators is absolutely crucial in today's world—as technology changes rapidly, capital markets move, and political and economic trends evolve. It's more important than ever to bring stakeholders together to have conversations, share best practices, and accelerate time to insight and time to action.</p>
  <p><strong>Natalya Guseva, World Economic Forum</strong></p>
</blockquote>`,
        },
      },
    ],
  },
  {
    id: "motive-6",
    slug: "mergermarket-motive-partners-swift-with-intelligence-exit",
    title:
      "Mergermarket: Motive Partners' swift With Intelligence exit built on tight alignment and strategic M&A execution",
    description:
      "Motive Partners partnered with With Intelligence to transform the business from a traditional B2B provider into a data-first analytics leader - culminating in a strategic $1.8 billion exit to S&P Global that highlights the power of close alignment and strategic M&A execution.",
    previewUrl: article05Image,
    previewType: PreviewType.Image,
    author: "Mergermarket",
    publishedDate: "2025-12-01",
    readingTime: 7,
    published: true,
    source: {
      label: "Mergermarket",
      url: "https://www.motivepartners.com/insights/mergermarket-motive-partners-swift-with-intelligence-exit-built-on-tight-alignment-and-strategic-m-a-execution",
    },
    sections: [
      {
        id: "section-intro",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Motive Partners partnered with With Intelligence to transform the business from a traditional B2B provider into a data-first analytics leader - culminating in a strategic $1.8 billion exit to S&amp;P Global that highlights the power of close alignment and strategic M&amp;A execution.</p>
<p>Read on to discover how this partnership accelerated growth, deepened market capabilities, and delivered an exceptional outcome.</p>`,
        },
      },
      {
        id: "section-accelerated-exit",
        type: ArticleSectionType.TITLE,
        content: {
          text: "An Accelerated Exit in an Era of Extended Hold Periods",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-timeline",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>In an era of lengthening private equity hold periods, Motive Partners' exit from With Intelligence after barely more than two years stands out. After acquiring a majority stake in the UK-based financial data and intelligence provider in July 2023 from Intermediate Capital Group (ICG), the fintech-focused investor sold to S&amp;P Global in a USD 1.8bn deal last month.</p>
<p>With Intelligence's accelerated development drew the attention of S&amp;P, one of Motive's key industry partners. The business "already had a robust proprietary data set," according to Motive. "We doubled down on that and further activated our value creation plan and executed the acquisition strategy."</p>
<p>The sale of With Intelligence comes at a time of heightened interest in financial data providers, in particular private markets-focused offerings. UK-based With Intelligence is expected to generate around USD 130m in revenue this year, which would imply a deal multiple above Preqin's multiple, around 13.8x its expected 2025 revenue.</p>`,
        },
      },
      {
        id: "section-alignment",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Tight Alignment with Management",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-partnership",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Nonetheless, an exit of this magnitude after a modest hold period required tight alignment with the portfolio company's management and rigorous execution. Early on, Motive's ambitions aligned closely with those of With Intelligence's founder-CEO Charlie Kerr, who had established the company in 1998 as Pageant Media, a traditional B2B content provider.</p>
<p>Even before ICG's investment in 2020, Kerr had begun transitioning the business from its B2B roots toward a data analytics-focused model, a strategic pivot that saw it rebrand as With Intelligence in 2021.</p>
<p>The arrival of Motive, a fintech specialist, accelerated that pivot. "From our perspective, what was exciting about Motive was their position in the ecosystem," Kerr said. "We knew there were relationships and partnerships we could develop."</p>
<p>Motive provided two operating partners, who played key roles in the execution of the value creation plan: Charles Teschner, former CEO of data and analytics solutions at BNY Mellon; and Andy Stewart, CEO of Wilshire. It also embedded other professionals into With Intelligence's team.</p>
<p>Kerr credits these moves with helping advance the company's strategic pivot. "They've brought a different level of operating and a different mindset that is much more software and data analytics-oriented," he said.</p>`,
        },
      },
      {
        id: "section-proprietary-data",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Building Proprietary Data Assets",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-data-strategy",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Before Motive invested, With Intelligence had begun to develop a proprietary dataset around LP intentions and preferences, collecting information from attendees at its industry events. Motive helped to optimize the capture of this data and build a model around it, which became a focus area for the value creation plan.</p>
<p>"You need to spend 80% of your time and resources getting the 20% of content that's unique," said Kerr, emphasizing the importance of having a proprietary dataset, especially as AI makes access to publicly available data easier and faster.</p>`,
        },
      },
      {
        id: "section-ma-execution",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Strategic M&A Execution",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-acquisitions",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Add-on acquisitions were central to the strategy. In 2023, With Intelligence bought UK-based FolioMetrics, which provides CRM and research management solutions to hedge funds and other firms. A year later it bought Sutton Place Strategies (SPS), a data and analytics provider to M&amp;A and PE professionals, from Bain &amp; Company. This acquisition saw With Intelligence expand into deal-sourcing, a strategic move on which it doubled down a year later with the acquisition of M&amp;A content platform The Deal from Delinian in early 2025. A further acquisition came with real assets specialist Realfin.</p>
<p>"Folio, SPS and The Deal were three really good deals and Motive strongly supported us in doing them," said Kerr.</p>`,
        },
      },
      {
        id: "section-spontaneous-deal",
        type: ArticleSectionType.TITLE,
        content: {
          text: "From Routine Dialogue to Strategic Transaction",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-deal-process",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Even as With Intelligence's private markets offering grew, when Motive went into a regular meeting with S&amp;P Global earlier this year, its intention was not to pitch the business. Motive regularly undertakes such conversations, giving the New York-based investment firm insight into how strategic players see the market. In this instance, what began as a routine dialogue between the fintech investor and one of its key industry partners quickly evolved into the outline of a deal.</p>
<p>"It's that intimate knowledge and operational focus that led us to the success of this deal," according to Motive. "It was clear that bringing With Intelligence into S&amp;P would create powerful market opportunities."</p>
<p>The spontaneous nature of the talks meant that there was no need to hold a broader process, just as Motive's investment in With Intelligence in 2023 had also bypassed a formal auction. Similarly, Motive also did not run a formal process for its exit of Global Shares to JPMorgan in 2022.</p>
<p>For a proprietary deal to succeed, the acquirer needs to have conviction that the target meets all its criteria, while the latter must trust in the buyer's ability to scale the business. "There are many intangibles – such as cultural fit – so this was a thoughtfully laid out journey, and finding the right home was very important."</p>`,
        },
      },
      {
        id: "section-future-outlook",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Looking Forward: AI and Private Markets Data",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-outlook",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Motive has no immediate plans to re-enter the financial data and intelligence space following this exit. Still, it sees long-term opportunities in the segment, including capital markets data providers and private markets-focused offerings.</p>
<p>"Over time the need for standardization and transparency in private markets holistically is so significant that there will be opportunities for us to keep playing in the space," according to Motive.</p>
<p>Beyond the explosion in demand for private markets data, AI is also reshaping the sector. Motive views AI as a tailwind for sector players, especially those with their own proprietary data.</p>
<p>"You will be pushed to be more creative in how you use a set of data and how you understand it," they noted. "The entrepreneurial dimension of using AI in proprietary data is alive and well."</p>`,
        },
      },
    ],
  },
  {
    id: "motive-5",
    slug: "the-with-intelligence-story",
    title: "The With Intelligence case study",
    description:
      "Discover how Motive Partners partnered with With Intelligence to build a global leader in asset management intelligence and data solutions.",
    previewUrl: "https://player.vimeo.com/video/1138910793",
    previewType: PreviewType.Video,
    author: "Motive Partners",
    publishedDate: "2026-01-01",
    readingTime: 3,
    published: true,
    source: {
      label: "Motive Partners Insights",
      url: "https://www.motivepartners.com/insights/the-with-intelligence-story",
    },
    sections: [
      {
        id: "section-overview",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Overview",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-intro",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>In November 2025, S&amp;P Global completed its <strong>$1.8 billion acquisition</strong> of With Intelligence, a leading provider of private markets data and analytics.</p>
<p>This transaction demonstrates Motive's IOI model in action—transforming a high-quality information services business into a scaled data and analytics company with real scarcity value. The video captures the full story: from identifying the opportunity during a challenging macro environment in 2023, to deploying deep operational expertise and innovation capabilities, to delivering a strategic exit in half the typical timeframe.</p>
<blockquote>
  <p>Earlier this month, we announced the planned acquisition of With Intelligence. With Intelligence brings an incredible amount of differentiated data on private markets, including extensive data in private equity, private credit, infrastructure, hedge funds and family offices. This unique combination will allow S&amp;P Global to provide essential intelligence to customers that they will not be able to get from any other provider. The team at With Intelligence has built a truly incredible company. Our goal is to create the most comprehensive solution for private markets participants anywhere in the world.</p>
  <p><strong>Martina Cheung, CEO of S&amp;P Global</strong></p>
</blockquote>
<blockquote>
  <p>Financial services are evolving. Data has become the differentiator, and we built something rare. That scarcity value made this exit possible.</p>
  <p><strong>Neil Cochrane, Partner, Motive Partners</strong></p>
</blockquote>`,
        },
      },
    ],
  },
  {
    id: "motive-1",
    slug: "motive-presents-about-motive-partners",
    title: "About Motive Partners",
    description:
      "At Motive Partners, we exist to accelerate the next generation of financial technology.",
    previewUrl: article04Image,
    previewType: PreviewType.Image,
    author: "Motive Partners",
    publishedDate: "2025-01-15",
    readingTime: 8,
    published: true,
    source: {
      label: "Motive Partners",
      url: "https://www.motivepartners.com/about",
    },
    sections: [
      {
        id: "section-intro",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>At Motive Partners, we exist to accelerate the next generation of financial technology. We back ambitious founders and management teams who are transforming how financial services are built, delivered and experienced, from the earliest stages of company formation through to scaled, mission-critical platforms.</p>
<p>Our investment strategy is focused and disciplined. On the <strong>early-stage side</strong> (Motive Ventures), we invest in pre-seed to Series A companies, typically leading or co-leading rounds with <strong>$1-10M</strong> checks. Motive Ventures three core areas:</p>
<ol class="pl-6">
<li><strong>AI and automation</strong></li>
<li><strong>Embedded finance</strong></li>
<li><strong>Platforms</strong></li>
</ol>
<p>For <strong>more mature businesses</strong>, our <strong>Growth &amp; Buyout</strong> strategy focuses on proven financial technology platforms. We actively partner with founders and management teams to scale growth, execute buyouts, and drive legacy transformations. Growth and buyout core areas:</p>
<ul class="pl-6">
<li>Banking &amp; payments</li>
<li>Wealth &amp; asset management</li>
<li>Insurance</li>
<li>Capital markets</li>
<li>AI, data &amp; analytics</li>
<li>Business services</li>
</ul>
<p>What differentiates Motive is how deeply we are embedded in the industry. Our team brings together leading financial services and technology expertise across investors, operators and innovators. This collective strength underpins our value creation strategy from investment selection and thesis development through to execution and exit. We don't just provide capital; we leverage our network to open doors, create customer connectivity and provide access to the people, platforms and opportunities that matter. We call our model &quot;IOI&quot; - Investing, Operating and Innovating.</p>`,
        },
      },
      {
        id: "the-ioi-model",
        type: ArticleSectionType.TITLE,
        content: {
          text: "The IOI Model",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-ioi",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Through this proven and repeatable approach, we integrate investment conviction with hands-on execution. We use data-driven insights to shape strategy, then work closely with leadership teams to translate that strategy into measurable results. Our aim is to lift growth, increase efficiency and enhance product, building stronger businesses faster.</p>
<p>Our capabilities span the full value creation toolkit. On the growth side, we help management teams design and execute transformative growth engines, aligning product, pricing, marketing and sales around clear priorities. We refine go-to-market strategies, unlock revenue opportunities and build scalable frameworks that drive both immediate impact and durable advantage.</p>
<p>Beyond growth, we bring specialist capabilities in AI, data and analytics, technology, operations, and talent and organizational design.</p>`,
        },
      },
      {
        id: "our-capabilities",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Our Capabilities",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-capabilities",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>We help companies harness AI and data to sharpen decision-making and create differentiated products. We work with technology and operations teams to modernize architecture, streamline processes and improve performance. And we partner with leadership on talent and organization, ensuring strategy, structure and culture are aligned to deliver sustainable success.</p>
<p>Through our <strong>Create</strong> platform, we further accelerate impact and deliver change. Create is how we embed the expertise, tools and support required for transformation directly into our portfolio companies. By working side-by-side with leadership teams, we help turn ambition into execution, and strategy into measurable outcomes.</p>
<p>In everything we do, our objective is simple: to be the most effective partner to financial technology businesses at every stage of their journey. By combining sector specialization, deep industry connectivity and an IOI model built on real operating experience, Motive Partners is uniquely positioned to help shape the future of financial services.</p>
<div class="border-t border-gray-200 mt-8 pt-8">
  <p class="disclaimer text-text/secondary">The information included herein is provided for illustrative purposes only. Nothing herein should be construed to imply that the company will be profitable or successful in executing on its business strategy. The information included herein is subject to change, even materially, without notice and should not be construed as business, investment, tax, accounting or legal advice. There can be no assurance that any of the outcomes described herein will materialize, in whole or in part. The information included herein has been collected from third party sources and has not been independently verified. Motive and its affiliates make no representation or warranty as to the accuracy or completeness of such information and are under no obligation to update such information beyond the date of this presentation.</p>
</div>`,
        },
      },
    ],
  },
  {
    id: "motive-2",
    slug: "challenges-and-opportunities-in-capital-markets",
    title: "Challenges and opportunities in capital markets",
    description:
      "Capital markets are essential to the global financial system, enabling businesses, governments, and investors to raise capital, fund projects and manage risks.",
    previewUrl: article03Image,
    previewType: PreviewType.Image,
    author: "Motive Ventures",
    publishedDate: "2024-12-10",
    readingTime: 15,
    published: true,
    source: {
      label: "Motive Ventures Insights",
      url: "https://www.motivepartners.com/insights/challenges-and-opportunities-in-capital-markets",
    },
    sections: [
      {
        id: "section-intro",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Capital markets are essential to the global financial system, enabling businesses, governments, and investors to raise capital, fund projects and manage risks. However, their interconnectedness, regulatory demands, and complex operational environments often constrain innovation.</p>
<p>Over decades, capital markets have transitioned from manual, intermediary-driven processes to more automated systems. The 1980s and 1990s brought electronic trading and post-trade systems, increasing efficiency. But despite advancements like automation, low latency systems, standardization, straight-through processing (STP), and cloud platforms over the last two decades, progress has been uneven. Key areas such as private markets, fixed income, and post-trade services lag in technological adoption due to legacy systems, regulatory challenges, and fragmented ecosystems.</p>
<p>The barriers to transformation are both structural and cultural. The complexity of global markets makes change operationally risky, as failures in critical processes - like US debt market settlements - could have catastrophic consequences. Regulations typically prioritize stability and careful evaluation of new technologies like blockchain or AI, while market participants often favor short-term stability over long-term strategic innovation.</p>
<p>This thought piece explores how new technologies can achieve a future-ready system to overcome barriers and enable incumbents to embrace change. We will outline key sub-segments that we are actively focusing on, which we believe hold significant potential to drive transformative change in capital markets.</p>`,
        },
      },
      {
        id: "what-does-this-mean-for-early-stage-innovation",
        type: ArticleSectionType.TITLE,
        content: {
          text: "What Does This Mean For Early-Stage Innovation In Capital Markets?",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-early-stage",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>For Motive Ventures, choosing the right companies to invest in is driven by our conviction of their right to win: do they have a vision, resources, expertise, track record, and market entry strategy that is fully aligned to the current operating environment to deliver value through transformational change? For new innovators, we believe the following factors can heavily weigh on making sure disruptive technology in capital markets is set up for the highest chance of success.</p>",
        },
      },
      {
        id: "thinking-global-from-day-one",
        type: ArticleSectionType.TITLE,
        content: {
          text: "1. Thinking Global From Day One",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-thinking-global",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Capital markets operate on a global scale, creating opportunities for fintech innovators but also demanding strategic decisions. Early-stage companies must decide whether to build globally scalable solutions or specialize in addressing specific regional and asset class nuances. Compliance and licensing are significant hurdles, often making scalability a challenge. Successful innovators require both a global vision and a phased go-to-market strategy to grow sustainably.</p>
<ul class="pl-6">
  <li><strong>Capital markets operate on a global scale</strong>, linking economies and institutions in a massive and intricate web of connected ecosystems. Traditionally, this interconnectedness means operational or technological change tends to involve large scale change management programs, and failure carries the potential for widespread disruption. Early-stage companies must decide whether to build globally scalable solutions or specialize in addressing specific regional and asset class nuances.</li>
  <li><strong>Capital markets require a high level of nuances</strong> across asset classes and geographies. In Europe, for example, navigating different capital market unions and exchanges adds significant complexity.</li>
  <li><strong>Lastly, one should understand regulatory impacts:</strong> Establishing compliance frameworks and obtaining licenses can be prohibitively expensive for young companies, often making scalability a formidable challenge.</li>
</ul>`,
        },
      },
      {
        id: "navigating-a-high-complexity-ecosystem",
        type: ArticleSectionType.TITLE,
        content: {
          text: "2. Navigating A High-Complexity Ecosystem",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-high-complexity",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>The diversity in asset classes, regional regulations, and technological infrastructures makes innovation in capital markets difficult. New innovations and early-stage companies should:</p>
<ul class="pl-6">
  <li><strong>Serve the client without over-customization:</strong> Early-stage businesses need to deliver value without falling into the trap of building overly bespoke solutions that hinder deployment and scalability.</li>
  <li><strong>Adopt an open architecture:</strong> Innovators must understand how the solution fits within the ecosystem, and what existing systems it replaces, augments or integrates to. They must create systems that seamlessly connect with multiple stakeholders and existing infrastructure, ensuring their solutions are both compatible and adaptable. If a good solution already exists, do not replicate it, integrate to it.</li>
  <li><strong>Identify the beach head:</strong> Early-stage businesses should focus on a specific area of the value chain where there is a clear need, work alongside existing players who are motivated to change and are prepared to invest time and money for success. By emphasizing flexibility, connectivity, and strategic focus, early-stage firms can become indispensable players in the ecosystem.</li>
</ul>`,
        },
      },
      {
        id: "making-change-a-rational-decision",
        type: ArticleSectionType.TITLE,
        content: {
          text: "3. Making Change A Rational Decision",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-rational-decision",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Decision-makers in capital markets on the receiving, incumbent, end will prioritize stability and cost-effective improvements over high-risk, large-scale innovation. Early-stage innovators need to find ways to be able to cope with these decision dynamics.</p>
<ul class="pl-6">
  <li><strong>Demonstrating incremental value:</strong> Instead of proposing wholesale overhauls, innovators should focus on solving specific pain points with measurable outcomes.</li>
  <li><strong>Building trust through working with incumbents:</strong> Collaborating with existing players can provide early-stage companies with the momentum and credibility needed to gain market traction.</li>
  <li><strong>Showing the pathway to the end-state solution:</strong> Having the necessary subject matter expertise and demonstrated track record of success will help to convince buyers that a strategic solution can be delivered in the long term.</li>
</ul>
<p>Collaborations between innovators and incumbents leverage the strengths of both, combining market reach and operational knowledge with agility and cutting-edge technology. Together, they can create transformative, high-value solutions for the market.</p>`,
        },
      },
      {
        id: "selected-innovation-imperatives",
        type: ArticleSectionType.TITLE,
        content: {
          text: 'Selected "Innovation Imperatives" Within Capital Markets',
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-innovation-imperatives",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>As discussed, the bar to deliver benefits through innovation in capital markets is and will remain high and therefore building a sustainable business has its challenges. However, we do still see gaps in the market where new technologies will shape the ecosystem and can fundamentally change the way of business going forward.</p>",
        },
      },
      {
        id: "electronification-progress-in-capital-markets",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Electronification: Progress In Capital Markets",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-electronification",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Electronic trading is revolutionizing capital markets by automating trading processes and improving price transparency and liquidity across multiple asset classes. Despite advancements in some areas, many markets, such as fixed income, small-cap equities, alternatives, private credit and unlisted equities are in various stages of their electronification journey.</p>
<p>Markets such as fixed income are still heavily reliant on voice-based trading, which reflects the complexity of the products, the commercial incentives of the intermediaries, and the fragmentation of existing systems.</p>
<p>The inefficiencies inherent in this limit liquidity and restrict access to broader market participants. Sophisticated electronic trading solutions are now emerging, designed to address these inefficiencies - with solutions tailored to different needs by asset class and geography and focused on a more diverse set of buyer personas instead of simply institutional appetite.</p>
<p>By automating trading processes, these solutions enhance secondary market liquidity and improve accessibility for participants on a global scale. Additionally, we believe the financialization of emerging markets through these technologies represents a significant opportunity for market integration and growth.</p>
<p>Nevertheless, this industry thus far has been dominated by large E2E platforms, presenting their respective offerings as multi asset / multi geo on both the buy and the sell side covering many aspects of the market. While these players have created the first wave of innovation, we believe a second wave is currently in motion. While the concept of multi-asset, multi-geography solutions still stands, the reality remains fragmented. Why is that the implementation of multi-asset solutions has lagged thus far?</p>
<ul class="pl-6">
  <li><strong>Complex needs:</strong> Buy-side and sell-side users require different workflows depending upon their size, their location and the asset classes they trade.</li>
  <li><strong>Fragmented infrastructure:</strong> OMS/EMS platforms have evolved as point solutions, which solve these complex, but do not integrate to a common system architecture, creating a mosaic of disparate systems.</li>
  <li><strong>Decentralized decision-making:</strong> Global clients of these solutions tend not to make decisions that consolidate across multiple asset class, allowing decision makers with responsibilities at the trading level to choose the solution that best suits their needs.</li>
  <li><strong>Tight margins:</strong> Existing players struggle to reinvest in better technology and services. As a result, service quality and innovation have declined.</li>
  <li><strong>Fragmentation:</strong> There is no unified platform that serves multiple jurisdictions or asset types. Each player focuses on specific geographies or asset classes, leading to inefficiencies and dissatisfied customers.</li>
  <li><strong>Limited innovation:</strong> Decision-makers within asset servicing often prioritize incremental improvements within their own asset classes rather than embracing transformative solutions.</li>
</ul>
<p>Several of the incumbent solution providers have followed an acquisition strategy aimed to integrate offerings across asset classes and geographies, but consolidation into a core technology solution has been a challenge. A lack of investments, legacy architectures, and loss of subject matter expertise have limited the incumbents' ability to deliver the seamless experience that users expect. While this consolidation strategy has the promise of greater investment in innovation, improved responsiveness to customer needs and lower operating costs, the execution strategy has not yet been realized.</p>`,
        },
      },
      {
        id: "digitisation-innovation-in-share-and-fund-registry",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Digitisation: Innovation In Share And Fund Registry",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-digitisation",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>The asset servicing functions performed by registries, a critical function in capital markets, has seen margins eroded over the years due to intense competition and driven by the challenges of the legacy technology that is in operation today. The systems are hard to change and maintain, are built to operate within specific jurisdictions and regulatory frameworks, data is siloed, and manual processes remain commonplace to manage corporate actions. This makes it challenging to drive down operational costs and build contemporary digital experiences for clients.</p>
<p>Notwithstanding this, larger platforms and others have done a commendable job - but the market remains fragmented. Without a common, multi-jurisdictional platform to manage both public as well as private assets, the industry continues to suffer from inefficiencies, subpar services, and stagnation in innovation. But why is the status quo not working?</p>
<p>Therefore, there is an opportunity for a common platform: A single, multi-jurisdictional platform could bring together the core functions of asset servicing including functions like transaction processing and compliance for both public and private assets.</p>
<p>A combined approach would streamline operations, improve service quality, and lower costs. Technologies such as blockchain and cloud native platforms can allow for higher degrees of speed, transparency, security and unification while remaining customer centric.</p>`,
        },
      },
      {
        id: "the-financialization-of-new-asset-classes",
        type: ArticleSectionType.TITLE,
        content: {
          text: "The Financialization Of New Asset Classes: Private Credit",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-financialization",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>The emergence of new asset classes like private credit, crypto and carbon credits are reshaping capital markets, offering unique opportunities for both investors and infrastructure providers. Unlike traditional asset classes, these markets lack established market infrastructure. This is an advantage - allowing infrastructure to be built from the ground up, tailored to today's needs and using contemporary technologies. It is also a challenge - the lack of institutional-grade market technology will take time to build and is often under-estimated.</p>
<p>For example, private credit has seen explosive growth as an alternative to traditional fixed income, with institutional investors drawn to its higher yields and tailored structures post financial crisis 2008. Private credit's global AUM surpassed $1.5 trillion in 2023 and is forecasted to reach $2.8 trillion by 2028. Unlike traditional lending, private credit offers bespoke solutions tailored to borrowers' needs, including direct lending, mezzanine financing, distressed debt, and asset-backed lending.</p>
<p>However, as the asset class grows, it is casting its net wider through increasing number of distribution channels, wider set of lending lines and targeting a broader set of customers. This explosive growth presents unprecedented opportunities but also demands robust technological frameworks to support and scale the asset class effectively. Technology is fundamental to fueling this expansion increasing connectivity to distribution channels, enabling innovation across asset backed lending lines and enabling private credit to efficiently underwrite, originate and service customers. Key aspects of this are:</p>
<ul class="pl-6">
  <li><strong>API integration:</strong> Streamlining how private credit funds interact with balance sheets, enabling seamless connectivity for distribution partners and accelerating deal flow.</li>
  <li><strong>AI-driven credit agreements:</strong> Leveraging AI to digitize and analyze credit agreements, empowering stakeholders with enhanced control, real-time insights, and optimized deployment of capital.</li>
  <li><strong>End-to-end process automation:</strong> Digitizing every stage of the credit lifecycle to ensure operational efficiency, faster execution, and reduced costs.</li>
  <li><strong>Data standardization:</strong> Establishing a unified framework for valuation, risk assessment, and trading, simplifying syndication and securitization.</li>
  <li><strong>Real-time monitoring:</strong> Deploying advanced tools for real-time insights into private credit performance, enabling proactive decision-making.</li>
</ul>
<p>As private credit providers increasingly tap into consumer "flow" lending, the need for scalable technology becomes paramount. Supporting high-volume, low-value lending requires infrastructure capable of automating processes, minimizing risk, and delivering cost efficiencies. These advancements will not only unlock growth in consumer lending but also drive innovation across all facets of private credit.</p>`,
        },
      },
      {
        id: "conclusion",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Conclusion: The Future Of Innovation In Capital Markets",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-conclusion",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Innovation in capital markets is not just about technology - it is about having domain expertise, collaboration, strategic alignment, and incremental progress. By leveraging strategic partnerships, innovators can bridge the gap between cutting-edge technology and market complexity. This approach not only reduces risks but also ensures that innovations deliver value early while laying the groundwork for long-term transformation.</p>
<p>The future of capital markets lies in pragmatic, targeted innovation driven by collaboration and a deep understanding of the sector's unique challenges and opportunities. We have identified white spots and continue to look for those founders brave enough to kickstart innovation in one of the most challenging sub segments in financial services technology.</p>
<div class="border-t border-gray-200 mt-8 pt-8">
  <p class="disclaimer text-text/secondary">The information included herein is provided for illustrative purposes only. Nothing herein should be construed to imply that the company will be profitable or successful in executing on its business strategy. The information included herein is subject to change, even materially, without notice and should not be construed as business, investment, tax, accounting or legal advice. There can be no assurance that any of the outcomes described herein will materialize, in whole or in part. The information included herein has been collected from third party sources and has not been independently verified. Motive and its affiliates make no representation or warranty as to the accuracy or completeness of such information and are under no obligation to update such information beyond the date of this presentation.</p>
</div>`,
        },
      },
    ],
  },
  {
    id: "motive-3",
    slug: "forming-the-future-of-finance",
    title:
      "Forming the Future of Finance: Motive Partners' Philosophy and Vision for Tomorrow's Financial World",
    description:
      "At the heart of Motive Partners lies a fundamental belief: the financial services industry stands at an inflection point. With fintech representing a potential market value opportunity of $9.6 trillion by 2030, the question is no longer whether transformation will happen - it's who will lead it. And Motive Partners is betting that specialized expertise, operational excellence, and relentless innovation will be the defining forces shaping the next era of financial services.",
    previewUrl: article02Image,
    previewType: PreviewType.Image,
    author: "Motive Partners",
    publishedDate: "2025-01-08",
    readingTime: 5,
    published: true,
    sections: [
      {
        id: "section-intro",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>At the heart of Motive Partners lies a fundamental belief: the financial services industry stands at an inflection point. With fintech representing a potential market value opportunity of $9.6 trillion by 2030, the question is no longer whether transformation will happen - it's who will lead it. And Motive Partners is betting that specialized expertise, operational excellence, and relentless innovation will be the defining forces shaping the next era of financial services.</p>",
        },
      },
      {
        id: "section-1-title",
        type: ArticleSectionType.TITLE,
        content: {
          text: "A Philosophy Built on Three Pillars",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-1",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Motive Partners' approach is anchored in what they call the Investor-Operator-Innovator (IOI) model - a philosophy that fundamentally redefines how private equity engages with fintech companies. Rather than taking a traditional financial engineering approach, Motive brings together three distinct forces working in concert.</p>
<p>As investors, they leverage deep industry knowledge and disciplined underwriting to identify opportunities others might miss. But they don't stop there. The "Operator" pillar recognizes that capital alone doesn't create value in complex fintech businesses - seasoned operational leaders who understand market dynamics, go-to-market strategies, and data infrastructure are essential. Finally, the "Innovator" dimension ensures that portfolio companies don't just compete in today's market; they shape tomorrow's.</p>
<p>This trinity reflects a core conviction: specialization matters. In an industry as complex as financial technology, generalist approaches fall short. By focusing exclusively on fintech across five key subsectors - Wealth &amp; Asset Management, Banking &amp; Payments, Capital Markets, Insurance, and AI/Data &amp; Analytics - Motive has built unparalleled domain expertise. Their team doesn't just understand the problems financial institutions face; they've lived them.</p>
<p>The thesis is direct: the firms who solve the three-sided infrastructure challenge of digitizing asset managers, connecting them to wealth distribution channels, and enabling individual portfolio construction to include private markets, will capture disproportionate value. Doing so requires building what is missing: a comprehensive operating system for private markets that spans connectivity, data infrastructure, intelligence, marketplace technology, execution and processing capabilities (Fink, 2025).</p>`,
        },
      },
      {
        id: "section-2-title",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Unlocking Long-Term Value in a Transforming Landscape",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-2",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>The philosophy extends to how Motive creates value. The With Intelligence case exemplifies this approach: through disciplined value creation strategy, proprietary data optimization, and strategic add-on acquisitions, Motive transformed a high-quality information services business into a true data and analytics powerhouse - ultimately delivering a $1.8 billion exit to S&amp;P Global just two years after initial investment.</p>
<p>This is not deal-making for its own sake. It's about recognizing where markets are moving and positioning portfolio companies to lead that transformation. Whether it's through their $100 million investment in Electric Mind to accelerate AI-led transformation, or their partnership with FNZ to democratize wealth management at scale, every investment reflects a thesis about where financial services is headed.</p>`,
        },
      },
      {
        id: "section-3-title",
        type: ArticleSectionType.TITLE,
        content: {
          text: "The Future: AI, Democratization, and Borderless Finance",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-3",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Looking forward, Motive identifies three macro trends reshaping their investment thesis.</p>
<p>First, AI is not coming to fintech - it's already here, and it's reshaping everything from risk management to client experience. Motive sees proprietary data combined with AI capabilities as creating genuine competitive moats. Their belief: AI will be a tailwind for companies with differentiated datasets and the operational excellence to deploy it effectively.</p>
<p>Second, the democratization of alternatives is accelerating. As alternative assets approach $40 trillion globally, institutional wealth infrastructure must evolve. Motive's portfolio reflects this conviction - companies like FNZ, InvestCloud, and Lyra are all positioned to make alternatives accessible and manageable at unprecedented scale.</p>
<p>Third, financial services is becoming increasingly unbundled and recombined. Legacy monoliths are losing to specialized, integrated point solutions. This creates opportunity for focused technology providers and platforms willing to challenge incumbents.</p>`,
        },
      },
      {
        id: "section-4-title",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Forming, Not Following",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-4",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Perhaps most telling about Motive's philosophy is their language: they don't speak of "adapting to" fintech trends - they speak of "forming the future of finance." This reflects a fundamental belief that with the right team, capital, and operational rigor, fintech leadership is not predetermined. It can be built, strategically and deliberately, by investors willing to think differently.</p>
<p>As financial services undergoes its most significant transformation in decades, Motive Partners stands not as observers, but as architects - backing the companies and leaders who will define what finance looks like when the dust settles.</p>`,
        },
      },
    ],
  },
  {
    id: "motive-4",
    slug: "a-new-hope-what-gps-expect-from-private-equity-in-2026",
    title: "A new hope: What GPs expect from private equity in 2026",
    description:
      "After a challenging year marked by subdued fundraising, tariff volatility and a backlash against various initiatives in the world's largest private equity market, one could forgive GPs for setting expectations low for the 12 months ahead.",
    previewUrl: article03Image,
    previewType: PreviewType.Image,
    author: "Helen de Beer",
    publishedDate: "2026-01-05",
    readingTime: 5,
    published: true,
    source: {
      label: "Private Equity International",
      url: "https://www.privateequityinternational.com/a-new-hope-what-gps-expect-from-private-equity-in-2026/",
    },
    sections: [
      {
        id: "section-intro",
        type: ArticleSectionType.TEXT,
        content: {
          html: "<p>This has turned out to be far from the case. At the end of 2025, Private Equity International canvassed private equity managers to gauge their sentiment for 2026. Generally speaking, the outlook was positive. For the most part, GPs expect the liquidity jam to finally dissolve in 2026.</p>",
        },
      },
      {
        id: "section-1-title",
        type: ArticleSectionType.TITLE,
        content: {
          text: "A new hope: What GPs expect from private equity in 2026",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-1",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<blockquote>
  <p>I believe we're headed into a blockbuster year for dealmaking," said <strong>Tyler Wolfram, Managing Partner at Oak Hill Capital.</strong> - GPs are seeing early signs that the <strong>liquidity challenges</strong></p>
</blockquote>
<blockquote>
  <p><strong>Kalyani Harar, Director of Private Investments at Wellington Management:</strong> added: "I don't want to jinx anything, but there are a lot of companies that are looking for that additional liquidity and that are ready to go to market."</p>
</blockquote>
<blockquote>
  <p><strong>William Benjamin, Head of Alternative Solutions at HSBC Asset Management</strong>, noted: "After a period of being somewhat overlooked, private equity is entering 2026 with renewed momentum. Exit backlogs are easing."</p>
</blockquote>
<p>Benjamin added that factors including "modest" Fed rate cuts, lessening tariff uncertainty, growth in secondaries and improving IPO markets are underpinning a prolonged recovery in exits and distributions.</p>
<p>Of course, many GPs went into 2025 with similarly high hopes, telling PEI at the time that dealflow pipelines would become unclogged over the course of the year. This proved not to be the case, with a series of macro economic shocks spooking the market on multiple occasions. A little optimism is never a bad thing, however, and market sources believe dealmaking and fundraising could prove mutually catalytic.</p>
<blockquote>
  <p>LPs are demanding realisations before committing to new funds, which means GPs will need to get back into market - and that simply isn't possible without selling," said <strong>Oak Hill's Wolfram.</strong> "While GPs may take slightly less on individual deals, we believe doing so unlocks the ability to raise the next fund.</p>
</blockquote>
<p>According to PEI's Fundraising Report Q3 2025, private equity funds continued their years-long struggle in 2025, raising only $577.7 billion in the first three quarters. In 2026, some GPs believe this trend may reverse.</p>
<p>Xavier Robert, Partner and CIO of PEI Group owner Bridgepoint, expects a "reordering of the PE landscape" as firms try to raise their latest fund since the 2020-21 fundraising boom. Outperformance, however, maybe limited to those who have proven themselves through the tough times.</p>
<blockquote>
  <p>Managers with a good track record of exits and cash returns will raise," said <strong>Flor Kassai, Managing Partner and Head of Buyout at Inflexion.</strong> "Those with limited liquidity and heavier reliance on valuation marks will find fundraising materially more difficult, and will increasingly rely on fund extensions, asset transfers and smaller vehicles to remain active.</p>
</blockquote>
<blockquote>
  <p>We're now seeing a healthy shakeout," <strong>Michael Cloe, Managing Director and CEO of Charlesbank Capital.</strong> "The firms that will come through it strongest are the ones focused on the basics - building better companies and driving real enterprise value, not hoping for multiple expansion to do the work for them."</p>
</blockquote>`,
        },
      },
      {
        id: "section-2-title",
        type: ArticleSectionType.TITLE,
        content: {
          text: "Driven by innovation",
          level: 2,
          className: MOTIVE_SECTION_TITLE_CLASSES,
        },
      },
      {
        id: "section-2",
        type: ArticleSectionType.TEXT,
        content: {
          html: `<p>Several trends could shape fund inflows in 2026. For one, unstable tariff policies last year created a touch of uncertainty around the US, which could result in Europe seeing increased activity moving forwards.</p>
<blockquote>
  <p>The <strong>geographic narrative is shifting</strong>. LPs remain committed to the US, but are reassessing allocations and recognising Europe's potential. As a result, I expect Europe to see <strong>growing interest</strong> in 2026 and beyond.</p>
</blockquote>
<blockquote>
  <p>It's a complex yet attractive environment that offers tangible, <strong>real-economy opportunities</strong> in high-quality companies and sectors for investors with the expertise to navigate it.</p>
</blockquote>
<p>Artificial intelligence, meanwhile, is shaping investment activity, with first movers in the space standing to enjoy a headstart in 2026.</p>
<blockquote>
  <p><strong>AI transformation</strong> will become an essential part of the value creation playbook. Valuation gaps between <strong>AI haves and AI have-nots</strong> will only widen.</p>
</blockquote>
<p>Some GPs, however, warn that AI should only be approached with a solid investment strategy, rather than just a fear of missing out.</p>
<blockquote>
  <p>AI is profoundly changing the world, but valuations for first derivative AI bets are <strong>reminiscent of the dotcom bubble</strong>. PE firms that embrace AI to transform traditional sectors, rather than trying to pick AI-platform winners, should outperform over time.</p>
</blockquote>
<blockquote>
  <p>Firms that understand <strong>AI infrastructure as a strategy</strong> will outperform, while those chasing optics over operating systems will stall.</p>
</blockquote>
<p>As in prior years, it is impossible to know what the coming months will hold. It's reassuring, though, that in spite of ongoing difficulties, GPs are looking ahead with optimism.</p>`,
        },
      },
    ],
  },
];
